import { Component,  OnDestroy, OnInit } from '@angular/core';

import { Subscription, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { LicensesEnum } from '../../../../api/monitoring/licenses.enum';
import { SublicensesEnum } from '../../../../api/monitoring/sublicenses.enum';

import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';

import { AccountModel } from '../../../../Core/Users/models/account.model';
import { AccountManagementService } from '../../services/account-management.service';
import { Sublicenses, SubscriptionLicenses } from '../../models/monitoring.configs.model';
import { PartnerResellerIds } from '../../../../Core/Config/enums/partner-reseller-ids.enum';
import { GeoRestriction } from '@api/monitoring/model/monitoring-account-configuration-api.model';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { DataSelectDialogModel } from '../../../../Common/Dialogs/models/data-select-dialog.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { DataSelectTakeoverPanelComponent } from '../../../../Common/Dialogs/components/DataSelectTakeoverPanel/data-select-takeover-panel.component';
import { PreferenceCountrySelectDialogService } from '@Admin/AccountManagement/services/preference-country-select-dialog.service';
import { CountryApiModel } from '@api/journodex/model/country-api.model';


@Component({
    selector: 'agpr-broadcast-preferences',
    templateUrl: './broadcast-preferences.component.html',
    styleUrls: ['./broadcast-preferences.component.scss'],
    providers: [
        PreferenceCountrySelectDialogService
    ],
    standalone: false
})
export class BroadcastPreferencesComponent implements OnDestroy, OnInit {

    account: AccountModel;
    licenses: SubscriptionLicenses;
    sublicenses: Sublicenses;

    tvEyesGeos = new IncludeExcludeModel<string>();
    tvEyesArchiving = false;
    tvEyesDownloading = false;

    geoRestriction: GeoRestriction[];
    isReadOnly: boolean = false;
    overlayContainer: Element;

    private subscriptions: Subscription[] = [];

    readonly id = 'broadcast-preferences';
    readonly licensesEnum = LicensesEnum;
    readonly sublicensesEnum = SublicensesEnum;
    readonly partnerResellerIds = PartnerResellerIds;

    constructor(
        public accountManagementService: AccountManagementService,
        public journodexRegionsService: JournodexRegionsService,
        private coreDialogService: CoreDialogService,
        private countrySelectDialogService: PreferenceCountrySelectDialogService,
    ) { }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.accountManagementService.getLicenses().subscribe(licenses => {
                this.licenses = licenses;
            }),
            this.accountManagementService.getSublicenses().subscribe(sublicenses => {
                this.sublicenses = sublicenses;
            }),
            this.accountManagementService.getTvEyesOptions().pipe(filter(x => !!x)).subscribe(x => {
                this.tvEyesGeos = x.geos;
                this.tvEyesArchiving = x.isArchiveProvisioned;
                this.tvEyesDownloading = x.isDownloadProvisioned;
            }),
            this.accountManagementService.getGeoRestrictions().subscribe(geos => {
                this.geoRestriction = geos;
            }),
            this.accountManagementService.getReadOnly().subscribe(x => {
                this.isReadOnly = x;
            }),
            this.accountManagementService.getSelectedAccount().subscribe(account => {
                this.account = account;
            })
        );
    }

    tvEyesToggle() {
        this.accountManagementService.notifyLicensesChanged();
    }

    tvEyesOptionsChange() {
        this.accountManagementService.setTvEyesOptions({
            geos: this.tvEyesGeos,
            isArchiveProvisioned: this.tvEyesArchiving,
            isDownloadProvisioned: this.tvEyesDownloading
        });

        let tyEyesGeo = this.geoRestriction.find(x => x.license === LicensesEnum.TVEYES);

        if (!tyEyesGeo) {
            tyEyesGeo = new GeoRestriction(LicensesEnum.TVEYES);
            this.geoRestriction.push(tyEyesGeo);
        }

        tyEyesGeo.includeCountryIds = this.tvEyesGeos.include
            .map(id => Number(this.journodexRegionsService.getCountryByIsoCode(id)?.id))
            .filter(Boolean);

        tyEyesGeo.excludeCountryIds = this.tvEyesGeos.exclude
            .map(id => Number(this.journodexRegionsService.getCountryByIsoCode(id)?.id))
            .filter(Boolean);

        this.accountManagementService.setGeoRestrictions(this.geoRestriction);
    }

    refMediaSublicenseChange() {
        this.accountManagementService.refMediaSublicenseChange();
    }

    showCountryBrowser(includeOrExclude: IncludeOrExclude) {

        this.countrySelectDialogService.prepareData(includeOrExclude, this.tvEyesGeos[includeOrExclude]);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io: DataSelectDialogModel = {
            inputs: {
                id: `${this.id}_countrySelectDialog`,
                dialogService: this.countrySelectDialogService,
                propagateSelection: true,
                dialogHeader: 'AMT.broadcast.countries.header',
                dialogMessage: 'AMT.broadcast.countries.message',
                leftColumnHeader: 'AMT.broadcast.lhp.heading',
                rightColumnHeader: 'AMT.broadcast.rhp.heading',
                lockMessage: 'Discover.dialogs.browsers.lockedGeo',
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedCountries: CountryApiModel[]) => {
                    if (!selectedCountries) {
                        selectedCountries = [];
                    }

                    this.tvEyesGeos[includeOrExclude] = selectedCountries.map(x => x.countryCode);
                    this.tvEyesGeos = cloneDeep(this.tvEyesGeos);
                    this.tvEyesOptionsChange();
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
